// client/src/pages/Unsubscribe.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, Mail } from 'lucide-react';
import SEOLayout from '../components/layout/SEOLayout';

const Unsubscribe = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState({
        isLoading: true,
        isSuccess: false,
        error: null,
        email: null
    });

    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setStatus({
                isLoading: false,
                isSuccess: false,
                error: 'Invalid unsubscribe link. No token provided.',
                email: null
            });
            return;
        }

        handleUnsubscribe();
    }, [token]);

    const handleUnsubscribe = async () => {
        try {
            const response = await fetch('/api/newsletter/unsubscribe', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token }),
            });

            const result = await response.json();

            if (response.ok && result.success) {
                setStatus({
                    isLoading: false,
                    isSuccess: true,
                    error: null,
                    email: result.email
                });
            } else {
                throw new Error(result.message || 'Failed to unsubscribe');
            }
        } catch (error) {
            setStatus({
                isLoading: false,
                isSuccess: false,
                error: error.message || 'Something went wrong. Please try again.',
                email: null
            });
        }
    };

    return (
        <SEOLayout
            title="Unsubscribe from Newsletter"
            description="Unsubscribe from Louis Silverstein's newsletter"
            noIndex={true}
        >
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                                <Mail className="h-6 w-6 text-blue-600" />
                            </div>

                            <h1 className="text-2xl font-bold text-gray-900 mb-6">
                                Newsletter Unsubscribe
                            </h1>

                            {status.isLoading && (
                                <div className="text-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                                    <p className="text-gray-600">Processing your request...</p>
                                </div>
                            )}

                            {status.isSuccess && (
                                <div className="text-center">
                                    <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-4" />
                                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                                        Successfully Unsubscribed
                                    </h2>
                                    {status.email && (
                                        <p className="text-gray-600 mb-4">
                                            <strong>{status.email}</strong> has been removed from our newsletter.
                                        </p>
                                    )}
                                    <p className="text-gray-600 mb-6">
                                        You will no longer receive newsletter emails from us.
                                        We're sorry to see you go!
                                    </p>

                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                        <p className="text-sm text-blue-800">
                                            <strong>Changed your mind?</strong><br/>
                                            You can always resubscribe on our homepage or contact us directly.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {status.error && (
                                <div className="text-center">
                                    <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-4" />
                                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                                        Unsubscribe Failed
                                    </h2>
                                    <p className="text-red-600 mb-6">
                                        {status.error}
                                    </p>

                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                                        <p className="text-sm text-red-800">
                                            If you continue to have issues, please contact us at{' '}
                                            <a
                                                href="mailto:louis@baraqu.com"
                                                className="font-medium text-red-900 hover:text-red-700"
                                            >
                                                louis@baraqu.com
                                            </a>
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Link
                                    to="/"
                                    className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                                >
                                    Return to Homepage
                                </Link>

                                <Link
                                    to="/contact"
                                    className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                                >
                                    Contact Us
                                </Link>
                            </div>

                            <div className="mt-8 pt-6 border-t border-gray-200">
                                <p className="text-xs text-gray-500">
                                    For privacy and security, unsubscribe links can only be used once and may expire.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </SEOLayout>
    );
};

export default Unsubscribe;